import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Fade,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Slide,
  Snackbar,
  SwipeableDrawer,
  Tooltip,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import { Product, Variant } from "../../../models/Product";
import * as StoreApi from "../../../api/storeApi";
import { Store } from "../../../models/Store";
import { isOk } from "../../../result";
import useStyles from "./styles";
import * as ProductApi from "../../../api/productApi";
import { getSingleV2 } from "../../../api/productApi";
import { useTheme } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import { TenantProductEditor } from "./TenantProductEditor";
import * as lab from "@material-ui/lab";
import { Alert } from "@material-ui/lab";
import { ConfirmDialog } from "../../../components/dialogs/ConfirmDialog";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../store";
import { thunkFetchStores } from "../../../store/stores/actions";
import { AddCircle, AddShoppingCart, Link } from "@material-ui/icons";
import * as InventoryApi from "../../../api/storeInventoryApi";
import * as StoreInventoryApi from "../../../api/storeInventoryApi";
import { StoreInventoryItemEditor } from "../../stores/StoreList/components/StoreInventoryItemEditor";
import { Currency } from "../../../components/misc/Currency";
import { UserContext } from "../../../UserProvider";
import { Authenticated } from "../../../models/User";
import { ConnectToPriceTag } from "./ConnectToPriceTag";
import {
  ArrowDownward,
  ArrowUpward,
  AttachMoney,
  AutoAwesome,
  Check,
  ContentCopy,
  Fastfood,
  Info
} from "@mui/icons-material";
import MenuIcon from "@material-ui/icons/Menu";
import Divider from "@material-ui/core/Divider";
import { SecuredField } from "../../users/misc/SecuredField";
import { Barcode } from "../../../components/Barcode";
import { OrderUnit } from "../../../models/Order";
import MaterialTable from "@material-table/core";
import { noop } from "../../../utils";
import StoreIcon from "@material-ui/icons/Store";
import { SectionCard } from "../components/SectionCard";
import { StoreTable } from "./StoreTable";
import { ProductDetailSection } from "../../../components/product/ProductDetailSection";
import { ProductHeader } from "../../../components/product/ProductHeader";
import { useI18n } from "../../../hooks/useI18n";

interface ProductDetailsProps {
  productId: string;
  onSave: () => void;
  onDelete: () => void;
}

interface ProductStores {
  stores: any[];
  isLoading: boolean;
}

export const ProductDetails: FunctionComponent<ProductDetailsProps> = ({
  productId,
  onSave,
  onDelete
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { t } = useI18n();

  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [showEditSuccess, setShowEditSuccess] = useState<boolean>(false);
  const [product, setProduct] = useState<Product>({} as Product);

  // Drawer for user actions menu
  const [actionsOpen, setActionsOpen] = React.useState(false);
  const toggleActionsDrawer = () => {
    setActionsOpen(!actionsOpen);
  };

  const handleActionsOpen = () => {
    setActionsOpen(true);
  };

  const handleActionsClose = () => {
    setActionsOpen(false);
  };

  useEffect(() => {
    (async () => {
      const result = await getSingleV2(productId);
      if (isOk(result)) {
        setProduct(result.value as Product);
      }
    })();
  }, []);

  return product && product.name ? (
    <div>
      <ProductHeader
        product={product}
        setShowEdit={show => setShowEdit(show)}
        setShowDelete={show => setShowDelete(show)}
        storeProduct={false}
      />
      <Box pt={2}>
        <ProductDetailSection product={product} mobile={mobile} />
      </Box>
      {product.variants && product.variants.length > 0 && (
        <Box pt={2}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SectionCard
                icon={
                  <AutoAwesome style={{ color: theme.palette.success.dark }} />
                }
                title={t("common.productDetails.variants")}
              >
                <MaterialTable
                  style={{ marginBottom: 20 }}
                  columns={[
                    {
                      title: t("common.formFields.image"),
                      field: "image",
                      render: (row: Variant) => {
                        return (
                          <div style={{ width: 80 }}>
                            <CardMedia
                              style={{
                                maxHeight: 50,
                                objectFit: "contain"
                              }}
                              component={"img"}
                              image={row?.image?.url}
                            />
                          </div>
                        );
                      }
                    },
                    { title: t("common.formFields.name"), field: "name" },
                    { title: t("common.formFields.price"), field: "price" },
                    {
                      title: t("common.formFields.gtin"),
                      field: "barcode"
                    },
                    {
                      title: t("common.formFields.articleNumber"),
                      field: "articleNumber"
                    }
                  ]}
                  data={product.variants as []}
                  components={{
                    Container: props => <Paper {...props} elevation={0} />
                  }}
                  options={{
                    showTitle: false,
                    search: false,
                    actionsColumnIndex: -1,
                    pageSize: product.variants.length,
                    paging: false
                  }}
                  localization={{
                    header: {
                      actions: ""
                    }
                  }}
                />
              </SectionCard>
            </Grid>
          </Grid>
        </Box>
      )}

      <Dialog
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"lg"}
        onClose={() => setShowEdit(false)}
        open={showEdit}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle
          disableTypography={true}
          style={{
            paddingBottom: 4,
            paddingTop: 8,
            borderBottom: `1px solid ${theme.palette.grey.A100}`,
            marginBottom: 10
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <Typography variant={"h6"}>
              {t("common.messages.editProduct")}
            </Typography>
            <IconButton
              aria-label="close"
              style={{ color: theme.palette.grey[500] }}
              onClick={() => setShowEdit(false)}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TenantProductEditor
            editingProduct={product}
            image={{ url: product.imageUrl as any }}
            onSave={async () => {
              const result = await getSingleV2(productId);

              if (isOk(result)) {
                setProduct(result.value as Product);
              }

              setShowEdit(false);
              setShowEditSuccess(true);
              onSave();
            }}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        visible={showDelete}
        titleText={t("common.actions.remove")}
        bodyText={t("common.productDetails.removeProductConfirmation")}
        confirmText={t("common.actions.remove")}
        onConfirm={async () => {
          const result = await ProductApi.remove(product.tenantInventoryItemId);

          if (result.type === "OK") {
            setShowDelete(false);
            onDelete();
          }
          return true;
        }}
        onCancel={() => {
          setShowDelete(false);
        }}
      />

      <Snackbar
        open={showEditSuccess}
        autoHideDuration={2000}
        onClose={() => {
          setShowEditSuccess(false);
        }}
      >
        <Alert onClose={() => setShowEditSuccess(false)} severity="success">
          {t("common.productDetails.productUpdated")}
        </Alert>
      </Snackbar>
    </div>
  ) : (
    <div
      style={{
        width: "100%",
        height: "100%",
        minHeight: "300px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <Fade
        in={true}
        style={{
          transitionDelay: "800ms"
        }}
        unmountOnExit
      >
        <CircularProgress size={100} />
      </Fade>
    </div>
  );
};
